import Util from "./../lib/util";
import Logger from "./../lib/logger";
import { RepositoryFactory } from "./../repository/RepositoryFactory";
import { makeCancellable, takeLatest } from "vuex-cancellable-actions";

const RadarJsonRepository = RepositoryFactory.get("radarJson");
const RadarGeojsonRepository = RepositoryFactory.get("radarGeojson");

const typeXrain = "xrain";
const typeCband = "cband";
const stateReadyRadar = "readyRadar";
const stateReadyMap = "readyMap";
const statePlay = "play";
const stateStop = "stop";
const stateNone = "none";
const timeFrame = [13, 7, 7];
const timeInterval = [5, 30, 60];

export default {
  namespaced: true,
  state: {
    /** 設定ファイル */
    radarConfig: null,
    /** レーダ雨量の種類 ['xrain':XRAIN, 'cband':Cバンドレーダ] */
    radarType: typeXrain,
    /** 透過率 [0 - 100%] */
    radarOpacity: 50,
    /** 地図の枚数 [1枚, 4枚] */
    radarNumber: 1,
    /** 地図の表示時間間隔（４枚表示）[5分, 30分, 60分] */
    radarInterval: 5,
    /** 履歴動画再生状態 ['none':無再生状態, 'readyRadar':レーダ準備状態, 'readyMap' 地図準備状態, 'play':再生状態, 'stop':停止状態] */
    historyPlayStatus: stateNone,
    /** 履歴動画再生終端日時 */
    historyPlayTime: new Date(),
    /** 履歴動画再生の再生時間間隔 [5分, 30分, 60分] */
    historyInterval: 5,
    /** 履歴動画再生コマ数 */
    historyPlayFrame: 13,
    /** 履歴動画再生コマ位置 */
    historyCurrentFrame: 0,
    /** 履歴動画再生コマ表示時間 */
    historyPlayFrameInterval: 2000,
    /** 履歴動画再生時間リスト */
    historyPlayList: null,
    /** レーダ表示レーダ画像情報(API応答) */
    radarImageApi: [],
    /** レーダ表示レーダ観測日時情報(JSONファイル) */
    radarImageDateJson: [],
    /** XRAINレーダ表示レーダメッシュ情報(JSONファイル) */
    radarXrainImageMeshMap: new Map(),
    /** Cバンドレーダ表示レーダメッシュ情報(JSONファイル) */
    radarCbandImageMeshMap: new Map(),
    /** レーダ表示レーダ画像情報(表示分) */
    radarImage: [],
    /** レーダ表示レーダ画像情報パラメータ */
    radarImageParam: null,
    /** レーダ観測日時(XRAIN) */
    radarXrainDate: null,
    /** レーダ観測日時(Cバンド) */
    radarCbandDate: null,
    /** レーダ最新観測日時(XRAIN) */
    radarNewXrainDate: null,
    /** レーダ最新観測日時(Cバンド) */
    radarNewCbandDate: null,
    /** レーダ観測日時パラメータ */
    radarDateParam: null,
    /** XRAIN定量範囲のライン */
    xrainLine: [],
    /** Cバンド定量範囲のライン */
    cbandLine: [],
  },
  mutations: {
    /** レーダ雨量設定の初期化 */
    setRadarConfig(state, config) {
      Logger.debug("[radar]mut - setRadarConfig()");
      state.radarConfig = config;
      state.radarType = config.type;
      state.radarOpacity = config.opacity * 100;
      state.radarNumber = config.number;
      state.radarInterval = config.interval;
      state.historyPlayFrameInterval = config.frameInterval;
    },
    /** レーダ雨量の種類設定 */
    setType(state, type) {
      Logger.debug("[radar]mut - setType(" + type + ")");
      state.radarType = type;
    },
    /** 透過率設定 */
    setOpacity(state, opacity) {
      Logger.debug("[radar]mut - setOpacity(" + opacity + ")");
      state.radarOpacity = opacity;
    },
    /** 地図の枚数設定 */
    setNumber(state, number) {
      Logger.debug("[radar]mut - setNumber(" + number + ")");
      state.radarNumber = number;
    },
    /** 地図の表示時間間隔設定 */
    setInterval(state, interval) {
      Logger.debug("[radar]mut - setInterval(" + interval + ")");
      state.radarInterval = interval;
    },
    /** 履歴動画再生状態リセット */
    resetHistoryPlay(state) {
      Logger.debug("[radar]mut - resetHistoryPlay()");
      state.historyPlayStatus = stateNone;
      state.historyCurrentFrame = 0;
      state.historyPlayList = null;
    },
    /** 履歴動画再生状態設定 */
    setHistoryPlayStatus(state, status) {
      Logger.debug("[radar]mut - setHistoryPlayStatus(" + status + ")");
      state.historyPlayStatus = status;
    },
    /** 履歴動画再生日時設定 */
    setHistoryPlayTime(state, time) {
      Logger.debug("[radar]mut - setHistoryPlayTime(" + time + ")");
      state.historyPlayTime = time;
    },
    /** 履歴動画再生の再生時間設定 */
    setHistoryMovieTime(state, movie) {
      Logger.debug("[radar]mut - setHistoryMovieTime(" + movie + ")");
      state.historyInterval = timeInterval[movie];
      state.historyPlayFrame = timeFrame[movie];
    },
    /** 履歴動画再生コマ位置設定 */
    setHistoryCurrentFrame(state, frame) {
      Logger.debug("[radar]mut - setHistoryCurrentFrame(" + frame + ")");
      state.historyCurrentFrame = frame;
    },
    /** 履歴動画再生時間リスト設定 */
    setHistoryPlayList(state, list) {
      Logger.debug("[radar]mut - setHistoryPlayList(list)");
      Logger.dir(list);
      state.historyPlayList = list;
    },
    /** レーダ画像情報一覧設定(API応答) */
    setRadarImageInfoApi(state, info) {
      Logger.debug("[radar]mut - setRadarImageInfoApi(info)");
      Logger.dir(info);
      state.radarImageApi = info;
    },
    /** レーダ観測日時情報一覧設定(JSONファイル) */
    setRadarImageDateJson(state, info) {
      Logger.debug("[radar]mut - setRadarImageDateJson(info)");
      Logger.dir(info);
      state.radarImageDateJson = info;
    },
    /** XRAINレーダメッシュ情報一覧設定(JSONファイル) */
    setRadarXrainImageMeshMap(state, { level, json }) {
      Logger.debug("[radar]mut - setRadarXrainImageMeshMap(" + level + "json)");
      Logger.dir(json);
      state.radarXrainImageMeshMap.set(level, json);
    },
    /** Cバンドレーダメッシュ情報一覧設定(JSONファイル) */
    setRadarCbandImageMeshMap(state, { level, json }) {
      Logger.debug("[radar]mut - setRadarCbandImageMeshMap(" + level + "json)");
      Logger.dir(json);
      state.radarCbandImageMeshMap.set(level, json);
    },
    /** レーダ画像情報一覧設定(表示分) */
    setRadarImageInfo(state, info) {
      Logger.debug("[radar]mut - setRadarImageInfo(info)");
      Logger.dir(info);
      state.radarImage = info;
    },
    /** レーダ画像情報一覧削除 */
    clearRadarImageInfo(state) {
      Logger.debug("[radar]mut - clearRadarImageInfo()");
      let info = [
        {
          obsDate: null,
          type: state.radarType,
          level: 0,
          areaInfo: null,
        },
      ];
      state.radarImage = info;
    },
    /** レーダ画像情報パラメータ設定 */
    setRadarImageInfoParam(state, param) {
      Logger.debug("[radar]mut - setRadarImageInfoParam(param)");
      Logger.dir(param);
      state.radarImageParam = param;
    },
    /** レーダ観測日時(XRAIN)設定 */
    setRadarXrainDate(state, date) {
      Logger.debug("[radar]mut - setRadarXrainDate(" + date + ")");
      state.radarXrainDate = date;
    },
    /** レーダ観測日時(Cバンド)設定 */
    setRadarCbandDate(state, date) {
      Logger.debug("[radar]mut - setRadarCbandDate(" + date + ")");
      state.radarCbandDate = date;
    },
    /** レーダ観測日時(XRAIN)設定 */
    setRadarNewXrainDate(state, date) {
      Logger.debug("[radar]mut - setRadarNewXrainDate(" + date + ")");
      state.radarNewXrainDate = date;
    },
    /** レーダ観測日時(Cバンド)設定 */
    setRadarNewCbandDate(state, date) {
      Logger.debug("[radar]mut - setRadarNewCbandDate(" + date + ")");
      state.radarNewCbandDate = date;
    },
    /** レーダ観測日時パラメータ設定 */
    setRadarDateParam(state, param) {
      Logger.debug("[radar]mut - setRadarDateParam(param)");
      Logger.dir(param);
      state.radarDateParam = param;
    },
    /** XRAIN定量範囲のライン設定 */
    setXrainLine(state, xrainLine) {
      Logger.debug("[radar]mut - setXrainLine(xrainLine)");
      Logger.dir(xrainLine);
      state.xrainLine = xrainLine;
    },
    /** Cバンド定量範囲のライン設定 */
    setCbandLine(state, cbandLine) {
      Logger.debug("[radar]mut - setCbandLine(cbandLine)");
      Logger.dir(cbandLine);
      state.cbandLine = cbandLine;
    },
  },
  actions: makeCancellable({
    /**
     * レーダ画像取得要求
     * @param {*} zoom ズームレベル
     * @param {*} time 観測日時
     * @param {*} current ture:最新, false:過去
     * @param {*} maxCoord 地図右上座標
     * @param {*} minCoord 地図左下座標
     * @return エリア情報
     */
    requestRadarImage: takeLatest(async function(
      { commit, state },
      { zoom, time, current, maxCoord, minCoord }
    ) {
      Logger.debug(
        "[radar]act - requestRadarImage(" +
          zoom +
          ", " +
          Util.formatDate(time) +
          ", " +
          current +
          ", [" +
          maxCoord[0] +
          "," +
          maxCoord[1] +
          "], [" +
          minCoord[0] +
          "," +
          minCoord[1] +
          "]" +
          ")"
      );
      // 地域レベル決定
      let level = convertLevel(state, zoom);
      // 取得枚数決定
      let number = state.radarNumber;
      // 取得時間リスト算出
      let frame = makeNumberList(state, time);
      commit("setHistoryPlayList", frame);
      // 表示間隔決定
      let interval = state.radarInterval;
      // 表示枚数に変更があれば初期化する
      if (
        null !== state.radarImageParam &&
        state.radarImageParam.number !== number
      ) {
        commit("clearRadarImageInfo");
      }

      // パラメータセット
      let param = {
        dispTime: Util.formatDate(time, "YYYY-MM-DD HH:mm"),
        type: state.radarType,
        level: level,
        number: number,
        interval: interval,
        play:
          state.historyPlayStatus !== stateStop &&
          state.historyPlayStatus !== stateNone,
      };
      let radarDateJson = null;
      let radarMeshJson = null;

      // レーダメッシュ情報設定
      if (param.type === typeXrain) {
        radarMeshJson = state.radarXrainImageMeshMap.get(level);
        // 未取得の場合のみファイルを取得する
        if (!radarMeshJson) {
          Logger.debug("[radar]getXrainRadarInfo()");
          const { data } = await RadarJsonRepository.getXrainRadarInfo(
            param.level
          );
          if (data != null) {
            Logger.dir(data, "[radar]getXrainRadarInfo() response");
            radarMeshJson = data;
            commit("setRadarXrainImageMeshMap", {
              level: level,
              json: radarMeshJson,
            });
          } else {
            Logger.warn("[radar]Didn't update radar info.");
            return;
          }
        }
      } else {
        radarMeshJson = state.radarCbandImageMeshMap.get(level);
        // 未取得の場合のみファイルを取得する
        if (!radarMeshJson) {
          Logger.debug("[radar]getCbandRadarInfo()");
          const { data } = await RadarJsonRepository.getCbandRadarInfo(
            param.level
          );
          if (data != null) {
            Logger.dir(data, "[radar]getCbandRadarInfo() response");
            radarMeshJson = data;
            commit("setRadarCbandImageMeshMap", {
              level: level,
              json: radarMeshJson,
            });
          } else {
            Logger.warn("[radar]Didn't update radar info.");
            return;
          }
        }
      }

      // 前回取得情報に変更があればレーダ情報を取得する
      if (
        null === state.radarImageParam ||
        (null !== state.radarImageParam &&
          (state.radarImageParam.dispTime != param.dispTime ||
            state.radarImageParam.type != param.type ||
            state.radarImageParam.level != param.level ||
            state.radarImageParam.number != param.number ||
            state.radarImageParam.interval != param.interval ||
            state.radarImageParam.play != param.play))
      ) {
        // レーダ観測日時情報取得
        if (current) {
          Logger.debug("[radar]getRadarDate()");
          const { data } = await RadarJsonRepository.getRadarDate();
          if (data != null) {
            Logger.dir(data, "[radar]getRadarDate() response");
            // XRAIN最新観測日時はエリア毎の最新観測日時の中から一番古い日時を設定する
            let xrainTimeList = [];
            xrainTimeList.push(data.xrainTime81);
            xrainTimeList.push(data.xrainTime82);
            xrainTimeList.push(data.xrainTime83);
            xrainTimeList.push(data.xrainTime87);
            xrainTimeList.push(data.xrainTime89);
            xrainTimeList.push(data.xrainTime90);
            const newXrainTime = getNewXrainTime(state, xrainTimeList);
            radarDateJson = {
              xrainTime: newXrainTime,
              cbandTime: data.cbandTime,
            };
          } else {
            Logger.warn("[radar]Didn't update radar info.");
            return;
          }
        } else {
          radarDateJson = { xrainTime: null, cbandTime: null };
        }
        // Store保存
        commit("setRadarImageDateJson", radarDateJson);
      } else {
        Logger.info("[radar]Radar param no difference.");
        // Storeのデータを使用
        radarDateJson = state.radarImageDateJson;
      }

      // 地図範囲内のレーダ画像情報を生成
      let radarImageDisp = [];
      for (let idx = 0; idx < number; idx++) {
        let reqDate = frame[idx];
        radarMeshJson.obsTime = reqDate;
        if (idx === number - 1 && current && radarDateJson != null) {
          if (param.type === typeXrain) {
            radarMeshJson.obsTime = Util.formatDate(
              radarDateJson.xrainTime,
              "YYYY-MM-DD HH:mm"
            );
          } else {
            radarMeshJson.obsTime = Util.formatDate(
              radarDateJson.cbandTime,
              "YYYY-MM-DD HH:mm"
            );
          }
        }

        let imageDisp;
        if (radarMeshJson.obsTime != null) {
          // レーダ情報設定
          imageDisp = makeRadarImage(
            reqDate,
            radarMeshJson,
            maxCoord,
            minCoord
          );
        } else {
          imageDisp = makeErrorRadarImage(reqDate);
        }
        Logger.dir(imageDisp, "[radar]Display data");
        radarImageDisp.push(imageDisp);
      }
      // Store保存
      commit("setRadarImageInfo", radarImageDisp);
      commit("setRadarImageInfoParam", param);
    }),
    /**
     * 履歴動画生成レーダ画像取得要求
     * @param {*} zoom ズームレベル
     * @param {*} maxCoord 地図右上座標
     * @param {*} minCoord 地図左下座標
     * @return エリア情報
     */
    requestHistoryPlayRadarImage: takeLatest(async function(
      { state, commit },
      { zoom, maxCoord, minCoord }
    ) {
      Logger.debug("[radar]act - requestHistoryPlayRadarImage(" + zoom + ")");

      // 地域レベル決定
      let level = convertLevel(state, zoom);
      // 取得枚数決定
      let number = state.historyPlayFrame;
      // 取得時間リスト算出
      let frame = makeHistoryList(state);
      commit("setHistoryPlayList", frame);
      // 表示間隔決定
      let interval = state.historyInterval;
      // 表示枚数に変更があれば初期化する
      if (
        null !== state.radarImageParam &&
        state.radarImageParam.number !== number
      ) {
        commit("clearRadarImageInfo");
      }

      // パラメータセット
      let param = {
        dispTime: Util.formatDate(state.historyPlayTime, "YYYY-MM-DD HH:mm"),
        obsTime: null,
        type: state.radarType,
        level: level,
        number: number,
        interval: interval,
        play:
          state.historyPlayStatus !== stateStop &&
          state.historyPlayStatus !== stateNone,
      };
      let radarMeshJson = null;

      // レーダメッシュ情報設定
      if (param.type === typeXrain) {
        radarMeshJson = state.radarXrainImageMeshMap.get(level);
        // 未取得の場合のみファイルを取得する
        if (!radarMeshJson) {
          Logger.debug("[radar]getXrainRadarInfo()");
          const { data } = await RadarJsonRepository.getXrainRadarInfo(
            param.level
          );
          if (data != null) {
            Logger.dir(data, "[radar]getXrainRadarInfo() response");
            radarMeshJson = data;
            commit("setRadarXrainImageMeshMap", {
              level: level,
              json: radarMeshJson,
            });
          } else {
            Logger.warn("[radar]Didn't update radar info.");
            return;
          }
        }
      } else {
        radarMeshJson = state.radarCbandImageMeshMap.get(level);
        // 未取得の場合のみファイルを取得する
        if (!radarMeshJson) {
          Logger.debug("[radar]getCbandRadarInfo()");
          const { data } = await RadarJsonRepository.getCbandRadarInfo(
            param.level
          );
          if (data != null) {
            Logger.dir(data, "[radar]getCbandRadarInfo() response");
            radarMeshJson = data;
            commit("setRadarCbandImageMeshMap", {
              level: level,
              json: radarMeshJson,
            });
          } else {
            Logger.warn("[radar]Didn't update radar info.");
            return;
          }
        }
      }

      // 地図範囲内のレーダ画像情報を生成
      let radarImageDisp = [];
      for (let idx = 0; idx < number; idx++) {
        let reqDate = frame[idx];
        radarMeshJson.obsTime = reqDate;

        let imageDisp;
        if (radarMeshJson.obsTime != null) {
          // レーダ情報設定
          imageDisp = makeRadarImage(
            reqDate,
            radarMeshJson,
            maxCoord,
            minCoord
          );
        } else {
          imageDisp = makeErrorRadarImage(reqDate);
        }
        Logger.dir(imageDisp, "[radar]Display data");
        radarImageDisp.push(imageDisp);
      }
      // Store保存
      commit("setRadarImageInfo", radarImageDisp);
      commit("setRadarImageInfoParam", param);

      // 地図準備状態
      commit("setHistoryPlayStatus", "readyMap");
    }),
    /**
     * レーダ観測日時取得要求
     * @param {*} time 観測日時
     * @param {*} current ture:最新, false:過去
     * @return レーダ観測日時
     */
    requestRadarDate: takeLatest(async function(
      { state, commit },
      { time, current }
    ) {
      Logger.debug(
        "[radar]act - requestRadarDate(" +
          Util.formatDate(time) +
          "," +
          current +
          ")"
      );

      let reqTime = Util.formatDate(time, "YYYY-MM-DD HH:mm");
      // パラメータセット
      let param = {
        dispTime: reqTime,
        xrainTime: reqTime,
        cbandTime: reqTime,
      };

      // APIコール
      Logger.debug("[radar]API - getRadarDate()");
      const { data } = await RadarJsonRepository.getRadarDate();
      if (data != null) {
        Logger.debug("[radar]API - response");
        Logger.dir(data);

        // Cバンドレーダ最新観測日時
        const newCbandTime = Util.formatDate(data.cbandTime);
        // XRAIN最新観測日時
        // エリア毎の最新観測日時の中から一番古い日時を設定する
        let xrainTimeList = [];
        xrainTimeList.push(data.xrainTime81);
        xrainTimeList.push(data.xrainTime82);
        xrainTimeList.push(data.xrainTime83);
        xrainTimeList.push(data.xrainTime87);
        xrainTimeList.push(data.xrainTime89);
        xrainTimeList.push(data.xrainTime90);
        const newXrainTime = getNewXrainTime(state, xrainTimeList);

        // 最新観測値をStore保存
        commit("setRadarNewXrainDate", newXrainTime);
        commit("setRadarNewCbandDate", newCbandTime);

        // カレント指定の場合のみ観測日時を最新観測日時にする
        if (current) {
          param.xrainTime = newXrainTime;
          param.cbandTime = newCbandTime;
        }
      } else {
        Logger.warn("[radar]API - don't response");
        param.xrainTime = null;
        param.cbandTime = null;
      }

      // Store保存
      commit("setRadarXrainDate", param.xrainTime);
      commit("setRadarCbandDate", param.cbandTime);

      // パラメータ保存
      commit("setRadarDateParam", param);
    }),
    /**
     * XRAIN定量範囲、Cバンド定量範囲のライン取得
     * @param {*} param0
     */
    getRadarLine: takeLatest(async function({ commit }) {
      Logger.debug("[radar]act - getRadarLine()");

      // APIからデータ取得
      // XRAIN
      let xrainLine = [];
      Logger.debug("[radar]API - getXrainLine()");
      const {
        data: xrainLineData,
        error: xrainLineError,
      } = await RadarGeojsonRepository.getXrainLine();
      Logger.debug("[radar]API - getXrainLine() - return");
      Logger.dir(xrainLineData);
      Logger.dir(xrainLineError);
      if (!xrainLineError) {
        xrainLine.push(xrainLineData);
      }

      // Cバンドレーダ
      let cbandLine = [];
      Logger.debug("[radar]API - getCbandLine()");
      const {
        data: cbandLineData,
        error: cbandLineError,
      } = await RadarGeojsonRepository.getCbandLine();
      Logger.debug("[radar]API - getCbandLine() - return");
      Logger.dir(cbandLineData);
      Logger.dir(cbandLineError);
      if (!cbandLineError) {
        cbandLine.push(cbandLineData);
      }

      // 取得情報をストアに格納
      commit("setXrainLine", xrainLine);
      commit("setCbandLine", cbandLine);
    }),
  }),
  getters: {
    /** 履歴動画再生中状態 */
    isPlay(state) {
      return state.historyPlayStatus === statePlay;
    },
    /** 履歴動画再生/準備中以外状態 */
    isNotPlay(state) {
      return (
        state.historyPlayStatus === stateNone ||
        state.historyPlayStatus === stateStop
      );
    },
    /** 履歴動画準備中状態 */
    isReady(state) {
      return (
        state.historyPlayStatus === stateReadyRadar ||
        state.historyPlayStatus === stateReadyMap
      );
    },
    /** 履歴動画MAP準備中状態 */
    isReadyMap(state) {
      return state.historyPlayStatus === stateReadyMap;
    },
    /** 履歴動画停止状態 */
    isStop(state) {
      return state.historyPlayStatus === stateStop;
    },
    /** 履歴動画未再生状態 */
    isNone(state) {
      return state.historyPlayStatus === stateNone;
    },
    /** 履歴動画終了時刻 */
    historyPlayEndTime(state) {
      return getHistoryPlayEndTime(state);
    },
  },
};

// 機能関数
/** 地域レベル変換 */
function convertLevel(state, zoom) {
  let config = state.radarConfig;
  let type = state.radarType;
  let level = 0;
  if (type === typeXrain) {
    if (zoom <= config.xrainZoomWide) {
      level = 1;
    } else if (zoom <= config.xrainZoomMiddle) {
      level = 2;
    } else if (zoom <= config.xrainZoomNarrow) {
      level = 3;
    }
  } else {
    if (zoom <= config.cbandZoomWide) {
      level = 1;
    } else if (zoom <= config.cbandZoomMiddle) {
      level = 2;
    } else if (zoom <= config.cbandZoomNarrow) {
      level = 3;
    }
  }

  return level;
}

/** 履歴動画終了時刻 */
function getHistoryPlayEndTime(state) {
  let end = state.historyPlayTime;
  if (state.radarType === typeXrain && state.radarXrainDate != null) {
    end = state.radarXrainDate;
  }
  if (state.radarType === typeCband && state.radarCbandDate != null) {
    end = state.radarCbandDate;
  }
  return end;
}

/** 表示リスト生成 */
function makeNumberList(state, baseTime) {
  let time = baseTime;
  let times = [];
  let intervalTimes = [];
  times.push(Util.formatDate(time, "YYYY-MM-DD HH:mm"));

  // 2枚目以降は、表示間隔分ずらした日時を5分間隔で切り捨てて格納
  // まず、基準時間から表示間隔分ずらした日時のリストを作成
  for (let i = 1; i < state.radarNumber; i++) {
    time = Util.subMinutes(time, state.radarInterval).toDate();
    intervalTimes.push(time);
  }
  // 表示間隔分ずらした日時は5分単位でないため、ファイルが存在する5分単位の値にする
  intervalTimes.forEach((iTime) => {
    iTime.setMinutes(Math.floor(iTime.getMinutes() / 5) * 5);
    times.push(Util.formatDate(iTime, "YYYY-MM-DD HH:mm"));
  });
  times = times.reverse();

  return times;
}

/** 履歴動画再生リスト生成 */
function makeHistoryList(state) {
  let time = getHistoryPlayEndTime(state);
  let times = [];
  times.push(Util.formatDate(time, "YYYY-MM-DD HH:mm"));
  let fraction = Number(Util.formatDate(time, "mm")) % state.historyInterval;
  if (fraction === 0) {
    fraction = state.historyInterval;
  }
  time = Util.subMinutes(time, fraction).toDate();
  for (let i = 1; i < state.historyPlayFrame; i++) {
    times.push(Util.formatDate(time, "YYYY-MM-DD HH:mm"));
    time = Util.subMinutes(time, state.historyInterval).toDate();
  }
  times = times.reverse();

  return times;
}

/** レーダ画像情報生成 */
function makeRadarImage(time, data, maxCoord, minCoord) {
  let areaInfo = [];
  let image = {
    reqDate: Util.toDate(time + ":00"),
    obsDate: Util.toDate(data.obsTime + ":00"),
    type: data.type,
    level: data.level,
    areaInfo: areaInfo,
  };
  for (const key in data.areaInfo) {
    const info = data.areaInfo[key];
    // 開始メッシュ(南西) AABB
    //   緯度 = AA / 1.5
    //   経度 = BB + 100
    // 終端メッシュ(北東) CCDD
    //   緯度 = (CC + 1) / 1.5
    //   経度 = BB + 101
    let AA = Math.floor(info.meshFrom / 100);
    let BB = info.meshFrom % 100;
    let CC = Math.floor(info.meshTo / 100);
    let DD = info.meshTo % 100;
    let item = {
      areaCd: info.areaCd,
      seq: info.seq,
      meshFrom: info.meshFrom,
      meshTo: info.meshTo,
      latMin: AA / 1.5,
      lngMin: BB + 100,
      latMax: (CC + 1) / 1.5,
      lngMax: DD + 101,
    };

    if (maxCoord != null && minCoord != null) {
      let mesh = {
        x: item.latMin,
        y: item.lngMin,
        w: item.latMax - item.latMin,
        h: item.lngMax - item.lngMin,
      };
      let map = {
        x: minCoord[0],
        y: minCoord[1],
        w: maxCoord[0] - minCoord[0],
        h: maxCoord[1] - minCoord[1],
      };
      let x = map.x - mesh.x;
      let y = map.y - mesh.y;
      if (-1 * map.w < x && x < mesh.w && -1 * map.h < y && y < mesh.h) {
        areaInfo.push(item);
      }
    } else {
      areaInfo.push(item);
    }
  }

  return image;
}

/** 取得失敗情報生成 */
function makeErrorRadarImage(time) {
  let areaInfo = [];
  let image = {
    reqDate: Util.toDate(time + ":00"),
    obsDate: null,
    type: null,
    level: null,
    areaInfo: areaInfo,
  };

  return image;
}

/** XRAIN最新観測日時算出 */
function getNewXrainTime(state, xrainTimeList) {
  // 遡る限界日時を設定
  let nowTime = Util.toDate();
  let limitTime = Util.subMinutes(
    nowTime,
    state.radarConfig.backTimeLimitMinutes
  ).toDate();

  // 限界日時内で一番過去の日時を最新観測日時とする
  let newXrainTime = null;
  xrainTimeList.forEach((xrainTime) => {
    // null以外の場合に、date型に変換して比較する
    if (!xrainTime) return;
    const targetDate = Util.toDate(xrainTime);
    if (
      targetDate.getTime() >= limitTime.getTime() &&
      (!newXrainTime || targetDate.getTime() < newXrainTime.getTime())
    ) {
      newXrainTime = targetDate;
    }
  });
  // 全て限界日時より過去の場合は限界日時を設定
  if (!newXrainTime) {
    newXrainTime = limitTime;
  }
  return Util.formatDate(newXrainTime);
}
